import type { ButtonHTMLAttributes, MouseEventHandler, ReactNode } from 'react';
import { cn } from '../lib/cn';

type ButtonColor = 'primary' | 'secondary' | 'default' | 'danger';
type ButtonVariant = 'solid' | 'flat' | 'outline' | 'ghost';

interface AppButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  /** Renders an icon before the button label */
  startIcon?: ReactNode;
  startContent?: ReactNode;
  color?: ButtonColor;
  variant?: ButtonVariant;
  onPress?: MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
}

/**
 * Opinionated button wrapper with a small local API surface.
 * Extend this to enforce your design system constraints.
 */
export function AppButton({
  children,
  className,
  color = 'primary',
  disabled,
  isLoading = false,
  onClick,
  onPress,
  startContent,
  startIcon,
  type = 'button',
  variant = 'solid',
  ...props
}: AppButtonProps) {
  const palette: Record<ButtonColor, Record<ButtonVariant, string>> = {
    primary: {
      solid: 'bg-primary text-primary-foreground hover:brightness-110',
      flat: 'bg-primary/15 text-primary hover:bg-primary/25',
      outline: 'border border-primary/50 text-primary hover:bg-primary/10',
      ghost: 'text-primary hover:bg-primary/10',
    },
    secondary: {
      solid: 'bg-secondary text-secondary-foreground hover:brightness-110',
      flat: 'bg-secondary/15 text-secondary hover:bg-secondary/25',
      outline: 'border border-secondary/50 text-secondary hover:bg-secondary/10',
      ghost: 'text-secondary hover:bg-secondary/10',
    },
    default: {
      solid: 'bg-iron-800 text-iron-100 hover:bg-iron-700',
      flat: 'bg-iron-800/70 text-iron-100 hover:bg-iron-700/80',
      outline: 'border border-iron-700 text-iron-200 hover:bg-iron-800/60',
      ghost: 'text-iron-200 hover:bg-iron-800/60',
    },
    danger: {
      solid: 'bg-danger text-white hover:brightness-110',
      flat: 'bg-danger/15 text-danger hover:bg-danger/25',
      outline: 'border border-danger/50 text-danger hover:bg-danger/10',
      ghost: 'text-danger hover:bg-danger/10',
    },
  };

  const icon = startIcon ?? startContent;

  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-60',
        palette[color][variant],
        className,
      )}
      disabled={disabled || isLoading}
      onClick={onPress ?? onClick}
      {...props}
    >
      {isLoading && (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      )}
      {!isLoading && icon}
      {children}
    </button>
  );
}
